import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  EmployeeAccount,
  EmployeeAccountSearchCommand,
  EmployeeAccountUpdate,
  EmployeeCandidate,
  EmployeeUserCandidate,
} from '../models/employee-account/employee-account';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class EmployeeAccountProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/employee-account';

  public search(
    command: EmployeeAccountSearchCommand,
    pageNumber: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: number,
  ): Observable<ApiResponse<PagedResult<EmployeeAccount>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<EmployeeAccount>>>(`${this.baseUrl}/search`, command, { params });
  }

  public getById(id: string): Observable<ApiResponse<EmployeeAccount>> {
    return this.httpClient.get<ApiResponse<EmployeeAccount>>(`${this.baseUrl}/${id}`);
  }

  /** Standard employees by code, name or user, flagged when already enabled. */
  public getCandidates(term: string | null): Observable<ApiResponse<EmployeeCandidate[]>> {
    let params = new HttpParams();
    if (term) params = params.set('term', term);
    return this.httpClient.get<ApiResponse<EmployeeCandidate[]>>(`${this.baseUrl}/candidates`, { params });
  }

  /** Nexus users that have no Standard employee yet. */
  public getUserCandidates(term: string | null): Observable<ApiResponse<EmployeeUserCandidate[]>> {
    let params = new HttpParams();
    if (term) params = params.set('term', term);
    return this.httpClient.get<ApiResponse<EmployeeUserCandidate[]>>(`${this.baseUrl}/user-candidates`, { params });
  }

  /** Creates the Standard employee of a Nexus user, named after it. */
  public createStandard(username: string): Observable<ApiResponse<EmployeeCandidate>> {
    return this.httpClient.post<ApiResponse<EmployeeCandidate>>(`${this.baseUrl}/standard-employee`, { username });
  }

  public enable(employeeReference: string): Observable<ApiResponse<EmployeeAccount>> {
    return this.httpClient.post<ApiResponse<EmployeeAccount>>(`${this.baseUrl}/enable/${employeeReference}`, null);
  }

  public update(id: string, payload: EmployeeAccountUpdate): Observable<ApiResponse<EmployeeAccount>> {
    return this.httpClient.put<ApiResponse<EmployeeAccount>>(`${this.baseUrl}/${id}`, payload);
  }
}
